import { WarningFilled } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Flex,
  Form,
  InputNumber,
  Segmented,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { LoadMore } from '../../components/LoadMore';
import { useCursorList } from '../../lib/use-cursor-list';
import { StockLocationPicker } from '../../components/StockLocationPicker';
import { StockLocationsCard } from './StockLocationsCard';
import { ApiError } from '../../lib/api-client';
import { formatDateTime, orDash } from '../../lib/format';
import { issueParts, listPartsRequests, type PartsRequest, type PartsRequestLine } from './api';

const { Text } = Typography;

type View = 'open' | 'backorder' | 'issued';

/**
 * The warehouse issue desk.
 *
 * Two things shape this screen:
 *
 *  - **Both numbers, always.** ADR 0006 makes AutoCount the authority for
 *    quantity, but its figure is read on a schedule and ours is live. Showing
 *    only one would be showing a number that is sometimes wrong; showing both
 *    with the read time lets the person holding the part decide.
 *  - **Issuing over a negative balance is possible and deliberate.** A
 *    technician standing at the counter with the part in their hand cannot be
 *    told "the system says there are none" — they will take it anyway and
 *    record nothing. The checkbox makes it a decision that leaves a trace.
 */
export function WarehousePage(): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [view, setView] = useState<View>('open');
  const [issuing, setIssuing] = useState<PartsRequest | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [acknowledge, setAcknowledge] = useState(false);
  const [locationUid, setLocationUid] = useState<string | undefined>(undefined);

  const requests = useCursorList(['parts-requests', view], (page) =>
    listPartsRequests({
      ...page,
      ...(view === 'open' ? { open: true } : {}),
      ...(view === 'backorder' ? { status: 'BACKORDER' as const } : {}),
      ...(view === 'issued' ? { status: 'ISSUED' as const } : {}),
    }),
  );

  const issue = useMutation({
    mutationFn: () =>
      issueParts(issuing?.uid ?? '', {
        ...(locationUid !== undefined ? { locationUid } : {}),
        lines: (issuing?.lines ?? [])
          .filter((line) => (quantities[line.uid] ?? 0) > 0)
          .map((line) => ({
            lineUid: line.uid,
            qty: String(quantities[line.uid] ?? 0),
            ...(acknowledge ? { acknowledgeNegative: true } : {}),
          })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['parts-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['job-card'] });
      setIssuing(null);
      void message.success(t('parts.issued'));
    },
    // The API names how short the shelf is. Showing it verbatim is why it
    // bothers to compute the number.
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('parts.issueFailed'));
    },
  });

  const shortfall = (line: PartsRequestLine): number => {
    const want = quantities[line.uid] ?? 0;
    const have = line.available === null ? 0 : Number(line.available);
    return want - have;
  };

  const anyShort = (issuing?.lines ?? []).some((line) => shortfall(line) > 0);

  const emptyFor: Record<View, string> = {
    open: t('parts.emptyOpen'),
    backorder: t('parts.emptyBackorder'),
    issued: t('parts.emptyIssued'),
  };

  return (
    <Flex vertical gap={16}>
      <Segmented<View>
        value={view}
        onChange={(value) => {
          setView(value);
        }}
        size="large"
        block
        options={[
          { value: 'open', label: t('parts.viewOpen') },
          { value: 'backorder', label: t('parts.viewBackorder') },
          { value: 'issued', label: t('parts.viewIssued') },
        ]}
      />

      {!requests.isFetching && requests.rows.length === 0 && (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyFor[view]} />
        </Card>
      )}

      {requests.rows.map((req) => (
        <Card
          key={req.uid}
          title={
            <Flex vertical gap={2}>
              <Flex align="center" gap={8} wrap>
                <Text strong style={{ fontSize: 17 }}>
                  {orDash(req.plateNo)}
                </Text>
                <Tag color={req.status === 'BACKORDER' ? 'error' : undefined}>
                  {t(`parts.status.${req.status}`)}
                </Tag>
              </Flex>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {req.requestNo} · {orDash(req.jobNo)} · {formatDateTime(req.requestedAt)}
              </Text>
            </Flex>
          }
          extra={
            req.status === 'ISSUED' ? null : (
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  setQuantities(
                    Object.fromEntries(
                      req.lines.map((line) => [
                        line.uid,
                        Math.max(0, Number(line.qtyRequested) - Number(line.qtyIssued)),
                      ]),
                    ),
                  );
                  setAcknowledge(false);
                  // Starts where the parts were put aside; another shelf releases that and takes from this one.
                  setLocationUid(req.locationUid ?? undefined);
                  setIssuing(req);
                }}
              >
                {t('parts.issue')}
              </Button>
            )
          }
        >
          <Table<PartsRequestLine>
            dataSource={req.lines}
            rowKey={(row) => row.uid}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            columns={[
              {
                title: t('parts.fields.part'),
                dataIndex: 'skuName',
                render: (name: string, row: PartsRequestLine) => (
                  <Flex vertical gap={2}>
                    <Text>{name}</Text>
                    <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
                      {row.skuCode}
                    </Text>
                  </Flex>
                ),
              },
              {
                title: t('parts.fields.wanted'),
                dataIndex: 'qtyRequested',
                width: 90,
                align: 'right' as const,
              },
              {
                title: t('parts.fields.given'),
                dataIndex: 'qtyIssued',
                width: 90,
                align: 'right' as const,
              },
              {
                title: t('parts.fields.onShelf'),
                dataIndex: 'available',
                width: 110,
                align: 'right' as const,
                render: (value: string | null) =>
                  value === null ? (
                    <Text type="secondary">—</Text>
                  ) : (
                    <Text strong={Number(value) <= 0} type={Number(value) <= 0 ? 'danger' : undefined}>
                      {value}
                    </Text>
                  ),
              },
              {
                // AutoCount is the authority (ADR 0006), so its figure sits
                // next to ours with the time it was read — never instead of it.
                title: t('parts.fields.autocount'),
                dataIndex: 'autocountQty',
                width: 170,
                align: 'right' as const,
                render: (value: string | null, row: PartsRequestLine) =>
                  value === null ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('parts.noAutocount')}
                    </Text>
                  ) : (
                    <Flex vertical align="end" gap={2}>
                      <Text>{value}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {t('parts.asOf', { when: formatDateTime(row.autocountReadAt) })}
                      </Text>
                    </Flex>
                  ),
              },
            ]}
          />
        </Card>
      ))}
      <LoadMore list={requests} />

      <ActionDrawer
        open={issuing !== null}
        title={t('parts.issueTitle', { plate: orDash(issuing?.plateNo) })}
        submitLabel={t('parts.issue')}
        submitting={issue.isPending}
        submitDisabled={anyShort && !acknowledge}
        onClose={() => {
          setIssuing(null);
        }}
        onSubmit={() => {
          issue.mutate();
        }}
        consequence={t('parts.issueConsequence', { jobNo: orDash(issuing?.jobNo) })}
      >
        <Form layout="vertical">
          <Form.Item>
            <StockLocationPicker value={locationUid} onChange={setLocationUid} />
          </Form.Item>
          {(issuing?.lines ?? []).map((line) => {
            const short = shortfall(line);
            return (
              <Form.Item
                key={line.uid}
                label={line.skuName}
                help={
                  short > 0 ? (
                    <Text type="danger">
                      {t('parts.shortBy', { count: short, available: line.available ?? '0' })}
                    </Text>
                  ) : undefined
                }
              >
                <InputNumber
                  min={0}
                  size="large"
                  style={{ width: '100%' }}
                  value={quantities[line.uid] ?? 0}
                  onChange={(value) => {
                    setQuantities((q) => ({ ...q, [line.uid]: value ?? 0 }));
                  }}
                  prefix={line.skuCode}
                />
              </Form.Item>
            );
          })}

          {anyShort && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningFilled />}
              style={{ marginBottom: 12 }}
              message={t('parts.shortTitle')}
              description={
                <Flex vertical gap={8}>
                  {/* The honest framing: AutoCount is the authority and its
                      figure may be a day old, so the person holding the part
                      decides — and the decision is recorded. */}
                  <Text>{t('parts.shortHelp')}</Text>
                  <Checkbox
                    checked={acknowledge}
                    onChange={(e) => {
                      setAcknowledge(e.target.checked);
                    }}
                  >
                    {t('parts.acknowledge')}
                  </Checkbox>
                </Flex>
              }
            />
          )}
        </Form>
      </ActionDrawer>

      <StockLocationsCard />
    </Flex>
  );
}
